import { useState, useEffect, useRef } from "react";
import { uploadFileToVps } from "@/lib/vpsStorage";

/* Types */
export type ServiceRow = {
  id: string;
  tenant_id: string;
  title: string;
  description: string | null;
  price_from: number | null;
  image_url: string | null;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
};

export type ServiceForm = {
  title: string;
  description: string;
  price_from: string;
  image_url: string;
  is_active: boolean;
};

interface Props {
  open: boolean;
  editing: ServiceRow | null;
  saving: boolean;
  tenantId: string;
  onClose: () => void;
  onSave: (form: ServiceForm) => void;
}

const EMPTY: ServiceForm = {
  title:       "",
  description: "",
  price_from:  "",
  image_url:   "",
  is_active:   true,
};

const INPUT_CLASS =
  "w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-800 placeholder-slate-300 outline-none focus:border-[#C8A96E]/60 focus:bg-white transition-all";

export default function ServiceModal({ open, editing, saving, tenantId, onClose, onSave }: Props) {
  const [form, setForm]     = useState<ServiceForm>(EMPTY);
  const [errors, setErrors] = useState<Partial<Record<keyof ServiceForm, string>>>();
  const [imgError, setImgError] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  /* Populate */
  useEffect(() => {
    if (!open) return;
    setErrors({});
    setImgError(false);
    setUploadError(null);
    if (editing) {
      setForm({
        title:       editing.title,
        description: editing.description ?? "",
        price_from:  editing.price_from != null ? String(editing.price_from) : "",
        image_url:   editing.image_url ?? "",
        is_active:   editing.is_active,
      });
    } else {
      setForm(EMPTY);
    }
  }, [open, editing]);

  /* Escape */
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const set = <K extends keyof ServiceForm>(key: K, val: ServiceForm[K]) =>
    setForm((p) => ({ ...p, [key]: val }));

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadError(null);
    try {
      const result = await uploadFileToVps({
        file,
        tenantId,
        folder: "website",
        allowedTypes: ["image/*"],
        maxSizeMB: 10,
      });
      if (!result.success || !result.url) {
        setUploadError(result.error || "Upload failed");
      } else {
        set("image_url", result.url);
        setImgError(false);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Upload failed";
      setUploadError(msg);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  /* Validate */
  const validate = (): boolean => {
    const e: Partial<Record<keyof ServiceForm, string>> = {};
    if (!form.title.trim()) e.title = "Title is required";
    if (form.price_from.trim() && isNaN(Number(form.price_from)))
      e.price_from = "Must be a valid number";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = () => {
    if (validate()) onSave(form);
  };

  if (!open) return null;

  const hasImageUrl = form.image_url.trim().length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-lg bg-white rounded-2xl border border-slate-200 overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#C8A96E]/10">
              <i className={`${editing ? "ri-edit-2-line" : "ri-add-line"} text-[#C8A96E]`}></i>
            </div>
            <h2 className="text-slate-800 font-semibold text-sm">
              {editing ? "Edit Service" : "New Service"}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-all cursor-pointer"
          >
            <i className="ri-close-line text-base"></i>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4 max-h-[68vh] overflow-y-auto">

          {/* Title */}
          <div>
            <label className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5 block">
              Title <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => set("title", e.target.value)}
              maxLength={80}
              placeholder="e.g. Wedding Photography Package"
              className={`${INPUT_CLASS} ${errors.title ? "border-red-300 focus:border-red-300" : ""}`}
              autoFocus
            />
            {errors.title && <p className="text-red-400 text-xs mt-1">{errors.title}</p>}
          </div>

          {/* Description */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-slate-500 text-xs font-medium uppercase tracking-wider">
                Description
              </label>
              <span className={`text-[10px] ${form.description.length > 350 ? "text-amber-500" : "text-slate-300"}`}>
                {form.description.length}/400
              </span>
            </div>
            <textarea
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              rows={3}
              maxLength={400}
              placeholder="Describe what is included in this service or package…"
              className={INPUT_CLASS + " resize-none"}
            />
          </div>

          {/* Starting price */}
          <div>
            <label className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5 block">
              Starting Price
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm select-none">$</span>
              <input
                type="text"
                inputMode="decimal"
                value={form.price_from}
                onChange={(e) => set("price_from", e.target.value)}
                placeholder="0.00"
                className={`${INPUT_CLASS} pl-7 ${errors.price_from ? "border-red-300 focus:border-red-300" : ""}`}
              />
            </div>
            {errors.price_from ? (
              <p className="text-red-400 text-xs mt-1">{errors.price_from}</p>
            ) : (
              <p className="text-slate-400 text-[10px] mt-1">Leave blank to hide the price on the public page</p>
            )}
          </div>

          {/* Image upload */}
          <div>
            <label className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5 block">
              Service Image
            </label>
            <div className={`relative rounded-xl border-2 border-dashed transition-all ${
              uploading ? "border-[#C8A96E]/40 bg-[#C8A96E]/5" : "border-slate-200 hover:border-[#C8A96E]/40 cursor-pointer"
            }`}>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                disabled={uploading}
                className="hidden"
                onChange={handleFileChange}
              />
              <div className="flex flex-col items-center gap-2 py-4 px-4 text-center">
                <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${
                  uploading ? "bg-[#C8A96E]/15 text-[#C8A96E]" : "bg-slate-100 text-slate-400"
                }`}>
                  {uploading ? (
                    <i className="ri-loader-4-line animate-spin text-sm"></i>
                  ) : (
                    <i className="ri-upload-line text-sm"></i>
                  )}
                </div>
                <p className="text-xs font-medium text-[#0f172a]">
                  {uploading ? "Uploading…" : "Click to upload image"}
                </p>
                {!uploading && (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-semibold bg-[#2563eb] text-white hover:bg-blue-700 transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-upload-line text-[10px]"></i>
                    Choose File
                  </button>
                )}
                {uploadError && (
                  <p className="text-red-500 text-[10px]">{uploadError}</p>
                )}
              </div>
            </div>
          </div>

          {/* Image URL or preview */}
          <div>
            <label className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5 block">
              Image URL
            </label>
            <input
              type="url"
              value={form.image_url}
              onChange={(e) => { set("image_url", e.target.value); setImgError(false); }}
              placeholder="https://your-domain.com/image.jpg"
              className={INPUT_CLASS}
            />
            <p className="text-slate-400 text-[10px] mt-1">
              Paste a direct link or upload above. Leave blank to show an icon instead.
            </p>

            {hasImageUrl && !imgError && (
              <div className="mt-2 rounded-lg overflow-hidden h-32 bg-slate-100 border border-slate-200 relative">
                <img
                  src={form.image_url}
                  alt="Preview"
                  className="w-full h-full object-cover object-top"
                  onError={() => setImgError(true)}
                  onLoad={() => setImgError(false)}
                />
              </div>
            )}
            {hasImageUrl && imgError && (
              <div className="mt-2 rounded-lg h-12 bg-red-50 border border-red-200 flex items-center gap-2 px-3">
                <i className="ri-image-line text-red-400 text-sm"></i>
                <p className="text-red-500 text-xs">Could not load image — check the URL</p>
              </div>
            )}
          </div>

          {/* Active toggle */}
          <div className="flex items-center justify-between py-3 px-4 rounded-xl bg-slate-50 border border-slate-200">
            <div>
              <p className="text-slate-700 text-sm font-medium">Visible on public page</p>
              <p className="text-slate-400 text-xs mt-0.5">
                {form.is_active ? "Visitors will see this service" : "Hidden — only you can see it"}
              </p>
            </div>
            <button
              type="button"
              onClick={() => set("is_active", !form.is_active)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${
                form.is_active ? "bg-emerald-500" : "bg-slate-200"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                  form.is_active ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-100">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-slate-500 hover:text-slate-700 border border-slate-200 rounded-lg transition-all cursor-pointer whitespace-nowrap"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || uploading}
            className="flex items-center gap-2 px-5 py-2 bg-[#C8A96E] hover:bg-[#b8965a] disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            {saving || uploading ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <i className="ri-save-line"></i>
            )}
            {saving || uploading ? "Saving…" : editing ? "Save Changes" : "Create Service"}
          </button>
        </div>
      </div>
    </div>
  );
}